import type { ElevationSourceQualification } from './elevationSourceQualification';
import type { Aqueduct4SourceClosure } from './aqueduct4';
/**
 * Refdata — nationwide (USA) reference-data store types.
 *
 * Refdata is the local-first replacement for unreliable federal/GIS APIs:
 * national datasets are harvested offline (scripts/data-ingest/refdata/),
 * partitioned into lat/lng grid cells, and stored in GCS under
 * `refdata/{datasetId}/{version}/cells/{cellId}.json.gz`, with
 * `refdata/{datasetId}/manifest.json` as the current-version pointer
 * (manifest-as-pointer, lazy invalidation — same convention as
 * EXTRACTION_MODEL_VERSION in src/services/knowledge/extractionCache.ts).
 *
 * This module is isomorphic (browser + server + ingest scripts) and must
 * stay dependency-free.
 */

export const REFDATA_DATASET_IDS = [
  'hifld-substations',
  'hifld-transmission',
  'gas-pipelines',
  'gas-compressors',
  'gas-interconnects',
  'gas-receipt-delivery',
  'usfws-critical-habitat',
  'nwi-wetlands',
  'nwi-mapping-coverage',
  'nps-nrhp',
  'nrcs-ssurgo',
  'fema-nfhl',
  'fema-source-context',
  'tiger-counties',
  'tiger-states',
  'epa-frs',
  'wbd-huc',
  'aquifers',
  'padus',
  'aiannh',
  'usgs-3dep',
  'wri-aqueduct',
] as const;

export type RefdataDatasetId = (typeof REFDATA_DATASET_IDS)[number];

/** Immutable source basis used by private pre-release SED/report acceptance. */
export interface RefdataAcceptanceSourceBinding {
  schemaVersion: 1;
  purpose: 'private-refdata-acceptance';
  digest: string;
  versions: Partial<Record<RefdataDatasetId, string>>;
  /** Canonical JSON hashes of the exact frozen manifests, including companions. */
  manifestHashes: Partial<Record<RefdataDatasetId, string>>;
}


export function isRefdataDatasetId(value: string): value is RefdataDatasetId {
  return (REFDATA_DATASET_IDS as readonly string[]).includes(value);
}

/**
 * How a dataset's features are stored across cells.
 * - 'whole': a feature spanning multiple cells is duplicated verbatim into
 *   each intersecting cell; readers dedupe by feature id.
 * - 'cell-clipped': geometry is clipped to the cell rectangle at ingest;
 *   fragments share an id but must NEVER be deduped (they partition the
 *   original feature — dedupe would drop area/length).
 */
export type RefdataGeometryStorage = 'whole' | 'cell-clipped';

export interface RefdataCellShard {
  id: string;
  /** SHA256 of the immutable compressed object bytes. */
  sha256: string;
  jsonBytes: number;
  featureCount: number;
}

/** Immutable cloud object identity; generation pins even a reused object name. */
export interface FemaSourceReceipt {
  path: string;
  generation: string;
  sha256: string;
  bytes: number;
}

export const FEMA_CONTEXT_RECEIPT_ROLES = [
  'nativeCatalog', 'transformInventory', 'sourceAccounting',
  'legacyLineage', 'conflicts', 'regulatoryContext',
] as const;
export type FemaContextReceiptRole = (typeof FEMA_CONTEXT_RECEIPT_ROLES)[number];

/**
 * Shared by the hazard and context manifests. Native archive closure, grid /
 * datum operations, unavailable masks, legacy topology, conflict accounting,
 * and panel / study / amendment joins each retain their own immutable receipt.
 * A download date or a single mapped fraction cannot replace these identities.
 */
export interface FemaSourceContextBinding {
  schemaVersion: 1;
  datasetId: 'fema-source-context';
  version: string;
  receipts: Record<FemaContextReceiptRole, FemaSourceReceipt>;
}

export const FEMA_SOURCE_CONTEXT_KINDS = [
  'native-mapped', 'native-unresolved', 'native-unavailable',
  'qualified-legacy-sfha', 'legacy-unavailable', 'source-conflict',
  'firm-panel', 'political-area', 'lomr',
] as const;
export type FemaSourceContextKind = (typeof FEMA_SOURCE_CONTEXT_KINDS)[number];

/** Exact two-layer source closure for the USFWS mapped-designation inventory. */
export interface CriticalHabitatSourceClosure {
  schemaVersion: 1;
  version: string;
  /** Complete occupied-cell inventory; a missing declared cell is an error. */
  cellIds: string[];
  layers: Array<{
    layerId: 0 | 2;
    designation: 'final' | 'proposed';
    sourceUrl: string;
    sourceCount: number;
    featureCount: number;
    vintageDate: string;
    raw: { path: string; generation: string; sha256: string; bytes: number };
  }>;
}

export interface CriticalHabitatFinding {
  designation: 'final' | 'proposed' | 'unknown';
  species: string;
  sourceLayerId?: 0 | 2;
  sourceFeatureId?: string;
  sourceUrl: string;
  listingStatus?: string;
  status?: string;
  publicationDate?: string;
  effectiveDate?: string;
  vacatedDate?: string;
}

export interface CriticalHabitatCoverage {
  status: 'complete' | 'partial' | 'unknown';
  reason: string;
  queryScope: 'point' | 'buffered-envelope' | 'multiple-parcels';
  layers: Array<{ layerId: 0 | 2; designation: 'final' | 'proposed'; status: 'complete' | 'partial' | 'unavailable'; sourceUrl: string; reason?: string }>;
}

export interface RefdataManifest {
  schemaVersion: 1 | 2;
  /** Schema 2 cannot be served by legacy schema-1 readers. */
  minimumReaderSchemaVersion?: 2;
  /** Complete, ordered replacement for each dense logical grid cell. */
  cellShards?: Record<string, RefdataCellShard[]>;
  datasetId: RefdataDatasetId;
  /** Harvest date, `YYYY-MM-DD`. Doubles as the immutable version folder name. */
  version: string;
  kind: 'vector' | 'raster-cog';
  /** Authoritative upstream (download page or service root). */
  sourceUrl: string;
  /** e.g. "HIFLD Open (DHS)", "USFWS National Wetlands Inventory". */
  sourceAuthority: string;
  license: string;
  /** Source-published / effective date (ISO) — stamped into EvidenceSection.dataAsOf. */
  vintageDate: string;
  harvestedAt: string;
  /** Vector: feature count. Raster: tile count. */
  featureCount: number;
  /** Vector cell size in degrees (0 for raster-cog). */
  cellSizeDeg: number;
  geometryStorage: RefdataGeometryStorage;
  attribution: string;
  /** Mapping footprint companion, bound to this exact snapshot version. */
  coverage?: { datasetId: 'nwi-mapping-coverage'; version: string };
  /** FEMA-specific evidence companion; never interchangeable with NWI mapping. */
  femaContext?: FemaSourceContextBinding;
  criticalHabitatSources?: CriticalHabitatSourceClosure;
  aqueduct4Sources?: Aqueduct4SourceClosure;
  padusNativeSources?: import('./padusNative').PadusNativeSourceClosure;
  /** Mixed publication and native datum qualification for raster samples. */
  elevationSourceQualification?: ElevationSourceQualification;
  notes?: string;
}

/** Minimal structural GeoJSON geometry — avoids a @types/geojson dependency. */
export type RefdataPosition = [number, number]; // [lng, lat]

export type RefdataGeometry =
  | { type: 'Point'; coordinates: RefdataPosition }
  | { type: 'MultiPoint'; coordinates: RefdataPosition[] }
  | { type: 'LineString'; coordinates: RefdataPosition[] }
  | { type: 'MultiLineString'; coordinates: RefdataPosition[][] }
  | { type: 'Polygon'; coordinates: RefdataPosition[][] }
  | { type: 'MultiPolygon'; coordinates: RefdataPosition[][][] };

export interface RefdataFeature {
  /** Stable upstream id (OBJECTID/GEOID/registry id) — dedupe key for 'whole' storage. */
  id: string;
  geom: RefdataGeometry;
  props: Record<string, string | number | null>;
}

export interface RefdataCellFile {
  schemaVersion: 1;
  datasetId: RefdataDatasetId;
  version: string;
  cell: string;
  features: RefdataFeature[];
}

// ---------------------------------------------------------------------------
// Query DTOs (shared by the server engine, the /api/refdata router, and the
// dual-use client).
// ---------------------------------------------------------------------------

export interface RefdataLatLng {
  lat: number;
  lng: number;
}

export interface RefdataBbox {
  minLat: number;
  minLng: number;
  maxLat: number;
  maxLng: number;
}

/** Attribute filter for near/bbox queries (exact-match semantics). */
export interface RefdataPropFilter {
  equals?: Record<string, string | number>;
  notEquals?: Record<string, string | number>;
}

/** Provenance stamped on every query result, from the dataset manifest. */
export interface NwiMappingCoverage {
  status: 'complete' | 'partial' | 'unknown';
  reason: string;
  version?: string;
  mappedFraction?: number;
}

export interface RefdataResultMeta {
  datasetId: RefdataDatasetId;
  version: string;
  vintageDate: string;
  sourceAuthority: string;
  coverage?: RefdataManifest['coverage'];
  femaContext?: FemaSourceContextBinding;
  criticalHabitatSources?: CriticalHabitatSourceClosure;
  aqueduct4Sources?: Aqueduct4SourceClosure;
  /** Mixed publication and native datum qualification for raster samples. */
  elevationSourceQualification?: ElevationSourceQualification;
}

export interface RefdataNearHit {
  feature: RefdataFeature;
  distanceKm: number;
}

export interface RefdataNearResult extends RefdataResultMeta {
  hits: RefdataNearHit[];
}

export interface RefdataBboxResult extends RefdataResultMeta {
  features: RefdataFeature[];
  /** True when the feature cap trimmed the result. */
  truncated: boolean;
}

export interface RefdataIntersectHit {
  feature: RefdataFeature;
  /** Present when the query asked for acreage (polygon datasets only). */
  intersectionAcres?: number;
}

export interface RefdataIntersectResult extends RefdataResultMeta {
  hits: RefdataIntersectHit[];
  /** Missing on legacy servers; cannot establish a complete screen. */
  truncated?: boolean;
}

/** Point-in-polygon query result (e.g. which county/watershed contains a point). */
export interface RefdataContainsResult extends RefdataResultMeta {
  features: RefdataFeature[];
  /** Explicit complete point-query receipt; older readers may omit this. */
  truncated?: boolean;
}

export interface RefdataElevationResult {
  /** Metres, order-preserving with the request; null = outside coverage / nodata. */
  elevations: Array<number | null>;
  source: 'refdata-3dep';
  version: string;
  /** Catalog source-publication summary, never query acquisition/dataAsOf. */
  vintageDate: string;
  sourceQualification?: ElevationSourceQualification;
}

export interface RefdataDatasetStatus {
  datasetId: RefdataDatasetId;
  available: boolean;
  version?: string;
  vintageDate?: string;
  featureCount?: number;
  cellSizeDeg?: number;
  kind?: RefdataManifest['kind'];
}

/** Exact original source records retained when identical NWI geometry carries
 * different declared acreage. None of these values is selected as authoritative. */
export interface NwiSourceAttributeVariant {
  sourceCode: string;
  sourceFid: number;
  fidColumn: string;
  sourceFeatureId: string;
  sourceLayer: string;
  sourceSrs: string;
  archive: { path: string; generation: string; sizeBytes: number; sha256: string; crc32c?: string };
  properties: Record<string, unknown> & { NWI_ID: string; ATTRIBUTE: string; WETLAND_TYPE: string; ACRES: number };
  geometryHash: string;
  propertiesHash: string;
  nativeWkbSha256: string;
  nativeWkbBytes: number;
}

export interface NwiSourceAcreageConflict {
  sourceFeatureId: string;
  sourceAcreageConflict: {
    status: 'unresolved-source-disagreement';
    field: 'ACRES';
    values: Array<{ sourceCode: string; sourceFid: number; value: number; propertiesHash: string }>;
  };
  sourceAttributeVariants: NwiSourceAttributeVariant[];
  sourceQualificationReceiptHash: string;
}

export interface NwiAcreageIssue { sourceFeatureId?: string; reason: string }
